import { Button } from '@1trading/react';
import { ListFilter, X } from 'lucide-react';
import { useState } from 'react';
import { deriveFilterOptions } from './apply-filters';
import { FilterEditDialog } from './filter-edit-dialog';
import type { FilterConfig, FilterState } from './types';

/** Badge count: number of set bounds for range/date, entry count otherwise. */
const chipCount = (values: string[] | undefined): number => (values ?? []).filter((value) => value !== '').length;

export interface TableFiltersProps<TRow> {
  configs: Array<FilterConfig<TRow>>;
  rows: TRow[];
  state: FilterState;
  onChange: (state: FilterState) => void;
}

export const TableFilters = <TRow,>({ configs, rows, state, onChange }: TableFiltersProps<TRow>) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const editing = configs.find((config) => config.key === editingKey) ?? null;
  const hasActiveFilters = Object.values(state).some((values) => chipCount(values) > 0);

  const applyFilter = (key: string, values: string[]) => {
    const { [key]: _removed, ...rest } = state;
    onChange(values.length > 0 ? { ...rest, [key]: values } : rest);
    setEditingKey(null);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
        <ListFilter className="size-4" />
        Filters
      </span>
      {configs.map((config) => {
        const count = chipCount(state[config.key]);
        // 'select' holds at most one value — show it directly rather than a bare "1", which
        // told the operator nothing about what's actually active.
        const badge = config.type === 'select' && count > 0 ? state[config.key]?.[0] : count > 0 ? count : null;
        return (
          <Button className="rounded-full" key={config.key} onClick={() => setEditingKey(config.key)} size="sm" type="button" variant={count > 0 ? 'default' : 'outline'}>
            {config.title}
            {badge !== null ? <span className="max-w-24 truncate rounded-full bg-background/20 px-1.5 text-xs tabular-nums">{badge}</span> : null}
          </Button>
        );
      })}
      {hasActiveFilters ? (
        <Button className="rounded-full gap-1.5" onClick={() => onChange({})} size="sm" type="button" variant="ghost">
          <X className="size-3.5" />
          Clear All
        </Button>
      ) : null}
      {editing ? (
        <FilterEditDialog
          helperText={editing.helperText}
          initial={state[editing.key] ?? []}
          key={editing.key}
          matchMode={editing.matchMode ?? 'exact'}
          onApply={(values) => applyFilter(editing.key, values)}
          onClose={() => setEditingKey(null)}
          options={editing.type === 'multi-choice' || editing.type === 'select' ? (editing.options ?? deriveFilterOptions(rows, editing)) : []}
          title={editing.title}
          type={editing.type}
        />
      ) : null}
    </div>
  );
};
TableFilters.displayName = 'TableFilters';
